/**
 * Global state management for authentication form using signals
 *
 * This store centralizes all authentication form state that was previously managed
 * locally in the AuthForm component with React hooks. It follows the same pattern
 * as other state stores in the application.
 *
 * Key features:
 * 1. Form field management (email, password, displayName)
 * 2. Loading and error states
 * 3. Tab state for signin/signup
 * 4. Form submission handlers that delegate to SessionState
 * 5. Form validation
 */

import type { SessionState } from './SessionState.js';

import { SOCIAL_ACCOUNT_NOT_FOUND_ERROR_CODE } from '#lib/social-auth-port.js';
import { formatPasswordRequirementsError } from '#lib/auth-error-utils.js';
import { createState } from '#lib/signals-utils.js';

export const SOCIAL_ACCOUNT_NOT_FOUND_MESSAGE =
  'We couldn’t find an Elara membership for this sign-in. Apply for membership to join.';

/** Shown when an operator has disabled the account (Firebase `auth/user-disabled`). */
export const ACCOUNT_DISABLED_MESSAGE = 'This account has been disabled. Please contact the Elara team.';

/** Map Firebase auth error codes to user-friendly messages. Uses a single message for wrong-password/user-not-found to avoid revealing whether the email exists. */
function authErrorMessage(error: unknown): string {
  const formatted = formatPasswordRequirementsError(error);
  if (formatted !== undefined) return formatted;

  const err = error as { code?: string; message?: string };
  const code = err?.code;
  if (typeof code === 'string') {
    switch (code) {
      case 'auth/wrong-password':
      case 'auth/user-not-found':
      case 'auth/invalid-credential': {
        return 'Incorrect email or password';
      }
      case 'auth/user-disabled': {
        return ACCOUNT_DISABLED_MESSAGE;
      }
      case 'auth/invalid-email': {
        return 'Please enter a valid email address';
      }
      case 'auth/too-many-requests': {
        return 'Too many attempts. Please try again later.';
      }
      case 'auth/email-already-in-use': {
        return 'An account already exists with this email. Try signing in.';
      }
      case SOCIAL_ACCOUNT_NOT_FOUND_ERROR_CODE: {
        return SOCIAL_ACCOUNT_NOT_FOUND_MESSAGE;
      }
      case 'auth/weak-password': {
        return 'Password should be at least 6 characters';
      }
      default: {
        break;
      }
    }
  }
  return err?.message && typeof err.message === 'string' ? err.message : 'Something went wrong. Please try again.';
}

export function createAuthFormState(sessionState: SessionState) {
  return createState({
    /** Form fields */
    email: '',
    password: '',
    displayName: '',
    firstName: '',
    lastName: '',
    /** Loading state for form submission */
    isLoading: false,
    /**
     * True from the moment a signup attempt starts until the page it started on has moved on.
     *
     * Distinct from `isLoading`, which covers only the auth call. Signup is a sequence — create the
     * account, then create the profile — and Firebase flips the auth state partway through it. The
     * web `App` redirects an authenticated visitor off an auth page, which mid-sequence lands them
     * on home and, because the profile has not arrived yet, a frame of onboarding, before the signup
     * page can send them to the payment step. This flag is what lets that redirect hold.
     *
     * Set BEFORE the auth call rather than between the two steps: the gap between `isLoading`
     * clearing and the profile create starting is a microtask, and a render landing inside it is
     * exactly the frame this exists to prevent. Deliberately NOT cleared when the sequence ends —
     * a failed profile create must keep the hold so its error stays on the form — so its only
     * reader pairs it with "the profile still does not exist".
     */
    isSigningUp: false,
    /** Error message from last submission attempt */
    error: undefined as string | undefined,
    /** Check if signin form is valid */
    get isSignInValid(): boolean {
      return this.email.length > 0 && this.password.length > 0;
    },
    /**
     * Check if signup form is valid. Policy acceptance is not a field: the signup form states that
     * continuing accepts the Terms and Privacy Policy, and the page records that acceptance on
     * submit, so there is nothing here to validate.
     */
    get isSignUpValid(): boolean {
      return this.email.length > 0 && this.password.length > 0 && this.firstName.length > 0 && this.lastName.length > 0;
    },
    /** Marks the start of a signup sequence. See {@link isSigningUp} for why this is not symmetric. */
    beginSignupSequence() {
      this.isSigningUp = true;
    },
    setEmail(email: string) {
      this.email = email;
      this.clearError();
    },
    setPassword(password: string) {
      this.password = password;
      this.clearError();
    },
    setDisplayName(displayName: string) {
      this.displayName = displayName;
    },
    setFirstName(firstName: string) {
      this.firstName = firstName;
      this.displayName = [firstName, this.lastName].filter(Boolean).join(' ');
    },
    setLastName(lastName: string) {
      this.lastName = lastName;
      this.displayName = [this.firstName, lastName].filter(Boolean).join(' ');
    },
    clearError() {
      this.error = undefined;
    },
    showSocialAccountNotFoundError() {
      this.error = SOCIAL_ACCOUNT_NOT_FOUND_MESSAGE;
    },
    clearForm() {
      this.email = '';
      this.password = '';
      this.displayName = '';
      this.firstName = '';
      this.lastName = '';
      this.error = undefined;
    },
    /** Whether the Apple sign-in button should be shown (iOS native only). */
    get isAppleSignInAvailable(): boolean {
      return sessionState.isAppleSignInAvailable;
    },
    /** Handle Google sign in */
    async signInWithGoogle(): Promise<boolean> {
      try {
        this.isLoading = true;
        this.error = undefined;
        const signedIn = await sessionState.signInWithGoogle();
        // A cancelled popup resolves rather than throwing, and must not read as success: signup
        // chains profile creation onto a true here, which would then run unauthenticated.
        if (!signedIn) return false;
        console.info('[AuthFormState] Google sign-in completed');
        this.clearForm();
        return true;
      } catch (error: unknown) {
        this.error = authErrorMessage(error);
        console.error('[AuthFormState] Google sign-in failed:', error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    /** Handle Apple sign in (iOS). A user-cancelled flow resolves without setting an error. */
    async signInWithApple(): Promise<boolean> {
      try {
        this.isLoading = true;
        this.error = undefined;
        const signedIn = await sessionState.signInWithApple();
        // A cancelled popup resolves rather than throwing, and must not read as success: signup
        // chains profile creation onto a true here, which would then run unauthenticated.
        if (!signedIn) return false;
        console.info('[AuthFormState] Apple sign-in completed');
        this.clearForm();
        return true;
      } catch (error: unknown) {
        this.error = authErrorMessage(error);
        console.error('[AuthFormState] Apple sign-in failed:', error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    /** Handle email sign in */
    async signInWithEmail() {
      if (!this.isSignInValid) {
        this.error = 'Please enter email and password';
        return;
      }
      try {
        this.isLoading = true;
        this.error = undefined;
        await sessionState.signInWithEmail(this.email, this.password);
        console.info('[AuthFormState] Email sign-in completed');
        this.clearForm();
      } catch (error: unknown) {
        this.error = authErrorMessage(error);
        console.error('[AuthFormState] Email sign-in failed:', error);
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Handle email sign up. Returns whether the account was created, so the signup page can go on
     * to create the profile — a cancelled social popup or a rejected password must not.
     */
    async signUpWithEmail(): Promise<boolean> {
      if (!this.isSignUpValid) {
        this.error = 'Please enter your name, email, and password';
        return false;
      }
      try {
        this.isLoading = true;
        this.error = undefined;
        await sessionState.signUpWithEmail(this.email, this.password, this.displayName);
        console.info('[AuthFormState] Email sign-up completed');
        this.clearForm();
        return true;
      } catch (error: unknown) {
        this.error = authErrorMessage(error);
        console.error('[AuthFormState] Email sign-up failed:', error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },
    /** Reset the form */
    reset() {
      this.clearForm();
      this.isSigningUp = false;
    },
  });
}

/**
 * The main reactive store for authentication form functionality
 *
 * @remark All the getters are auto-transformed into computed signals, the values are signals.
 * The functions are not touched, they are the actions on the state.
 */
export type AuthFormState = ReturnType<typeof createAuthFormState>;
